
import React, { useState, useEffect, useRef } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import { useAuth } from "../../Common/Configurations/AuthContext";
import { useNavigate } from "react-router-dom";
import airports from "../../Data/AirportList.json";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import SeriesFareTabs from "../../Supplier/SeriesFares/SeriesFareTabs"; // Import the tabs component
import { 
    RiFlightTakeoffLine, 
    RiSearchLine,
    RiCalendarLine,
    RiMoneyDollarCircleLine,
    RiUserLine,
    RiAddCircleLine,
    RiSaveLine,
    RiRefreshLine,
    RiCheckboxCircleLine,
    RiCheckboxBlankCircleLine,
    RiBuildingLine,
    RiPlaneLine,
    RiFilterLine,
    RiArrowUpLine,
    RiArrowDownLine,
    RiSettingsLine,
    RiCloseLine,
    RiInformationLine,
    RiCalendarCheckLine
} from "react-icons/ri";

const SeriesFare = () => {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    
    // Remove sidebar state and references
    // const [sidebarOpen, setSidebarOpen] = useState(false);
    
    // Search and route states
    const [fromSearch, setFromSearch] = useState("");
    const [showFromDropdown, setShowFromDropdown] = useState(false);
    const [filteredFromAirports, setFilteredFromAirports] = useState([]);
    const [selectedFromAirport, setSelectedFromAirport] = useState(null);
    const [routes, setRoutes] = useState([]);
    const [fetchingRoutes, setFetchingRoutes] = useState(false);
    
    // Filter for destinations
    const [destinationFilter, setDestinationFilter] = useState("");
    
    // Individual route-airline fares
    const [routeAirlineFares, setRouteAirlineFares] = useState({});
    
    // Bulk update states
    const [showBulkUpdate, setShowBulkUpdate] = useState(false);
    
    // Valid To days modal state
    const [showDaysModal, setShowDaysModal] = useState(false);
    const [selectedDaysBefore, setSelectedDaysBefore] = useState(1);
    const [currentDateContext, setCurrentDateContext] = useState({
        key: null,
        field: '',
        date: null,
        type: 'individual' // 'individual' or 'bulk'
    });
    
    // Get current date and calculate one day before
    const getCurrentDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };
    
    const getOneDayBeforeDate = () => {
        const today = new Date();
        const oneDayBefore = new Date(today);
        oneDayBefore.setDate(today.getDate() - 1);
        return oneDayBefore.toISOString().split('T')[0];
    };
    
    // Set default dates
    const currentDate = getCurrentDate();
    const oneDayBeforeDate = getOneDayBeforeDate();
    
    const [bulkUpdateValues, setBulkUpdateValues] = useState({
        validFrom: currentDate,
        validTo: oneDayBeforeDate,
        departureDate: currentDate,
        cabinClass: 'ECONOMY',
        pricePerPerson: '', // CHANGED: Now using single price field
        seats: ''
    });
    
    // UI states
    const [isSaving, setIsSaving] = useState(false);
    
    const fromInputRef = useRef(null);
    const mainContentRef = useRef(null);

    // Get user's currency from profile
    const userCurrency = user?.currency || 'INR';

    // Custom input component for date picker
    const CustomDateInput = React.forwardRef(({ value, onClick }, ref) => (
        <div className="input-group input-group-sm">
            <input
                type="text"
                className="form-control form-control-sm"
                value={value}
                onClick={onClick}
                ref={ref}
                readOnly
                placeholder="Select date"
                style={{ height: '31px', padding: '0.25rem 0.5rem' }}
            />
            <button className="btn btn-sm btn-secondary" type="button" onClick={onClick} style={{ height: '31px', padding: '0.25rem 0.5rem' }}>
                <RiCalendarLine />
            </button>
        </div>
    ));

    // Handle date change for Valid From
    const handleValidFromChange = (date) => {
        if (date) {
            const formattedDate = date.toISOString().split('T')[0];
            setBulkUpdateValues(prev => ({
                ...prev,
                validFrom: formattedDate
            }));
        }
    };

    // Handle date change for Valid To
    const handleValidToChange = (date) => {
        if (date) {
            const formattedDate = date.toISOString().split('T')[0];
            setBulkUpdateValues(prev => ({
                ...prev,
                validTo: formattedDate
            }));
        }
    };

    // Handle date change for Departure Date (for bulk update)
    const handleDepartureDateChange = (date) => {
        if (date) {
            const formattedDate = date.toISOString().split('T')[0];
            
            // Show modal to ask how many days before
            setCurrentDateContext({
                key: null,
                field: 'departureDate',
                date: formattedDate,
                type: 'bulk'
            });
            setSelectedDaysBefore(1); // Reset to default
            setShowDaysModal(true);
        }
    };

    // Handle days selection from modal
    const handleDaysSelect = () => {
        const { date, type, key } = currentDateContext;
        
        if (date) {
            const departureDateObj = new Date(date);
            const daysBeforeDate = new Date(departureDateObj);
            daysBeforeDate.setDate(departureDateObj.getDate() - selectedDaysBefore);
            const formattedDaysBefore = daysBeforeDate.toISOString().split('T')[0];
            
            if (type === 'bulk') {
                // Update bulk values
                setBulkUpdateValues(prev => ({
                    ...prev,
                    departureDate: date,
                    validTo: formattedDaysBefore
                }));
            } else if (type === 'individual' && key) {
                // Update individual fare
                setRouteAirlineFares(prev => ({
                    ...prev,
                    [key]: {
                        ...prev[key],
                        departureDate: date,
                        validTo: formattedDaysBefore,
                        validFrom: prev[key].validFrom || currentDate
                    }
                }));
            }
        }
        
        setShowDaysModal(false);
    };

    // Handle route-airline fare date changes
    const handleRouteAirlineDateChange = (key, field, date) => {
        if (date) {
            const formattedDate = date.toISOString().split('T')[0];
            
            if (field === 'departureDate') {
                // Show modal to ask how many days before
                setCurrentDateContext({
                    key: key,
                    field: 'departureDate',
                    date: formattedDate,
                    type: 'individual'
                });
                setSelectedDaysBefore(1); // Reset to default
                setShowDaysModal(true);
            } else {
                setRouteAirlineFares(prev => ({
                    ...prev,
                    [key]: {
                        ...prev[key],
                        [field]: formattedDate
                    }
                }));
            }
        }
    };

    // Days options for the modal
    const daysOptions = [1, 2, 3, 4, 5, 6, 7, 14, 30, 60, 90, 180, 365];

    // Calculate total price based on price per person and seats
    const calculateTotalPrice = (pricePerPerson, seats) => {
        const price = parseFloat(pricePerPerson) || 0;
        const seatCount = parseInt(seats) || 0;
        return (price * seatCount).toFixed(2);
    };

    // Filter airports for search
    useEffect(() => {
        if (fromSearch.trim() === "") {
            const topAirports = airports
                .filter(airport => airport && airport.display === true)
                .slice(0, 10);
            setFilteredFromAirports(topAirports);
        } else {
            const searchTerm = fromSearch.toLowerCase();
            const filtered = airports.filter(airport => {
                if (!airport) return false;
                
                const airportCode = (airport.airportCode || '').toLowerCase();
                const cityName = (airport.cityName || '').toLowerCase();
                const airportName = (airport.airportName || '').toLowerCase();
                
                return airportCode.includes(searchTerm) ||
                    cityName.includes(searchTerm) ||
                    airportName.includes(searchTerm);
            }).slice(0, 10);
            setFilteredFromAirports(filtered);
        }
    }, [fromSearch]);

    // Handle airport selection
    const handleFromSelect = (airport) => {
        if (!airport) return;
        
        setSelectedFromAirport(airport);
        setFromSearch(`${airport.cityName || ''} (${airport.airportCode || ''})`);
        setShowFromDropdown(false);
        
        // Fetch routes when airport is selected
        fetchRoutes(airport.airportCode);
    };

    // Fetch routes from backend
    const fetchRoutes = async (airportCode) => {
        if (!airportCode) return;
        
        setFetchingRoutes(true);
        try {
            const response = await apiService.get(`${config.GETROUTESFROM}/${airportCode}`);
            setRoutes(response || []);
            
            // Initialize route-airline fares with default values
            const initialFares = {};
            response.forEach(route => {
                // For each airline in this route, create a separate entry
                route.airlines.forEach(airline => {
                    const key = `${route.to}_${airline.iata}`;
                    initialFares[key] = {
                        to: route.to,
                        airlineCode: airline.iata,
                        airlineName: airline.name,
                        validFrom: currentDate,
                        validTo: oneDayBeforeDate,
                        departureDate: currentDate,
                        cabinClass: 'ECONOMY',
                        pricePerPerson: '', // CHANGED: Now using single price field
                        seats: '',
                        totalPrice: '', // This will be auto-calculated
                        selected: false
                    };
                });
            });
            setRouteAirlineFares(initialFares);
        } catch (error) {
            console.error('Error fetching routes:', error);
            setRoutes([]);
        } finally {
            setFetchingRoutes(false);
        }
    };

    // Handle route-airline fare changes (non-date fields)
    const handleRouteAirlineFareChange = (key, field, value) => {
        const updatedFare = {
            ...routeAirlineFares[key],
            [field]: value
        };

        // Auto-calculate total price when pricePerPerson or seats change
        if (field === 'pricePerPerson' || field === 'seats') {
            const pricePerPerson = parseFloat(field === 'pricePerPerson' ? value : updatedFare.pricePerPerson) || 0;
            const seats = parseInt(field === 'seats' ? value : updatedFare.seats) || 0;
            
            // Calculate total price (price per person * seats)
            const totalPrice = pricePerPerson * seats;
            updatedFare.totalPrice = totalPrice.toString();
        }
        
        setRouteAirlineFares(prev => ({
            ...prev,
            [key]: updatedFare
        }));
    };

    // Toggle route-airline selection
    const toggleRouteAirlineSelection = (key) => {
        setRouteAirlineFares(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                selected: !prev[key].selected
            }
        }));
    };

    // Select/deselect all route-airlines
    const selectAllRouteAirlines = () => {
        const updatedFares = { ...routeAirlineFares };
        Object.keys(updatedFares).forEach(key => {
            updatedFares[key].selected = true;
        });
        setRouteAirlineFares(updatedFares);
    };

    const deselectAllRouteAirlines = () => {
        const updatedFares = { ...routeAirlineFares };
        Object.keys(updatedFares).forEach(key => {
            updatedFares[key].selected = false;
        });
        setRouteAirlineFares(updatedFares);
    };

    // Apply bulk update to selected items
    const applyBulkUpdate = () => {
        const updatedFares = { ...routeAirlineFares };
        
        Object.keys(updatedFares).forEach(key => {
            if (updatedFares[key].selected) {
                Object.keys(bulkUpdateValues).forEach(field => {
                    if (bulkUpdateValues[field] !== '') {
                        // Special handling for departure date - update valid to as well
                        if (field === 'departureDate' && bulkUpdateValues.departureDate) {
                            const departureDateObj = new Date(bulkUpdateValues.departureDate);
                            const oneDayBefore = new Date(departureDateObj);
                            oneDayBefore.setDate(departureDateObj.getDate() - 1);
                            const formattedOneDayBefore = oneDayBefore.toISOString().split('T')[0];
                            
                            updatedFares[key].departureDate = bulkUpdateValues.departureDate;
                            updatedFares[key].validTo = formattedOneDayBefore;
                        } else {
                            updatedFares[key][field] = bulkUpdateValues[field];
                        }
                    }
                });
                
                // Recalculate total price if pricePerPerson or seats were updated
                if (bulkUpdateValues.pricePerPerson !== '' || bulkUpdateValues.seats !== '') {
                    const pricePerPerson = parseFloat(bulkUpdateValues.pricePerPerson !== '' ? bulkUpdateValues.pricePerPerson : updatedFares[key].pricePerPerson) || 0;
                    const seats = parseInt(bulkUpdateValues.seats !== '' ? bulkUpdateValues.seats : updatedFares[key].seats) || 0;
                    
                    const totalPrice = pricePerPerson * seats;
                    updatedFares[key].totalPrice = totalPrice.toString();
                }
            }
        });
        
        setRouteAirlineFares(updatedFares);
        setShowBulkUpdate(false);
        alert("✅ Bulk update applied to all selected items!");
    };

    // Reset everything
    const resetForm = () => {
        setFromSearch("");
        setSelectedFromAirport(null);
        setRoutes([]);
        setRouteAirlineFares({});
        setDestinationFilter("");
        setBulkUpdateValues({
            validFrom: currentDate,
            validTo: oneDayBeforeDate,
            departureDate: currentDate,
            cabinClass: 'ECONOMY',
            pricePerPerson: '', // CHANGED
            seats: ''
        });
        setShowBulkUpdate(false);
        setShowDaysModal(false);
    };

    // Scroll to top
    const scrollToTop = () => {
        if (mainContentRef.current) {
            mainContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Scroll to bottom
    const scrollToBottom = () => {
        if (mainContentRef.current) {
            mainContentRef.current.scrollTo({ top: mainContentRef.current.scrollHeight, behavior: 'smooth' });
        }
    };

    // Clear destination filter
    const clearDestinationFilter = () => {
        setDestinationFilter("");
    };

    // Validate form
    const validateForm = () => {
        if (!selectedFromAirport) {
            alert('Please select departure airport');
            return false;
        }
        
        const selectedItems = Object.keys(routeAirlineFares).filter(key => routeAirlineFares[key].selected);
        if (selectedItems.length === 0) {
            alert('Please select at least one airline-route combination');
            return false;
        }
        
        // Validate each selected route-airline
        for (const key of selectedItems) {
            const fare = routeAirlineFares[key];
            
            if (!fare.validFrom) {
                alert(`Please select Valid From date for ${fare.airlineCode} → ${fare.to}`);
                return false;
            }
            
            if (!fare.validTo) {
                alert(`Please select Valid To date for ${fare.airlineCode} → ${fare.to}`);
                return false;
            }
            
            if (!fare.departureDate) {
                alert(`Please select Departure Date for ${fare.airlineCode} → ${fare.to}`);
                return false;
            }
            
            if (new Date(fare.validFrom) > new Date(fare.validTo)) {
                alert(`Valid To must be after Valid From for ${fare.airlineCode} → ${fare.to}`);
                return false;
            }
            
            if (!fare.pricePerPerson || parseFloat(fare.pricePerPerson) <= 0) {
                alert(`Please enter price per person for ${fare.airlineCode} → ${fare.to}`);
                return false;
            }
            
            if (!fare.seats || parseInt(fare.seats) <= 0) {
                alert(`Please enter seats for ${fare.airlineCode} → ${fare.to}`);
                return false;
            }
            
            // Calculate total price based on price per person and seats
            const pricePerPerson = parseFloat(fare.pricePerPerson) || 0;
            const seats = parseInt(fare.seats) || 0;
            const calculatedTotal = pricePerPerson * seats;
            
            if (calculatedTotal <= 0) {
                alert(`Total price must be greater than 0 for ${fare.airlineCode} → ${fare.to}`);
                return false;
            }
        }
        
        return true;
    };

    // Handle bulk form submission
    const handleBulkSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;
        
        setIsSaving(true);
        
        try {
            // Group by same dates and cabin class for efficiency
            const fareGroups = {};
            
            Object.keys(routeAirlineFares).forEach(key => {
                const fare = routeAirlineFares[key];
                if (fare.selected) {
                    const groupKey = `${fare.validFrom}_${fare.validTo}_${fare.cabinClass}`;
                    
                    if (!fareGroups[groupKey]) {
                        fareGroups[groupKey] = {
                            companyId: user?.cid || user?.companyId,
                            from: selectedFromAirport.airportCode,
                            validFrom: fare.validFrom,
                            validTo: fare.validTo,
                            departureDate: fare.departureDate,
                            cabinClass: fare.cabinClass,
                            items: []
                        };
                    }
                    
                    // Calculate total price as price per person * seats
                    const pricePerPerson = parseFloat(fare.pricePerPerson) || 0;
                    const seats = parseInt(fare.seats) || 0;
                    
                    fareGroups[groupKey].items.push({
                        to: fare.to,
                        airlineCode: fare.airlineCode,
                        pricePerPerson: pricePerPerson, // CHANGED: Single price field
                        totalSeats: seats
                    });
                }
            });
            
            // Send each group to create
            let totalCreated = 0;
            let totalFailed = 0;
            
            for (const key in fareGroups) {
                try {
                    const response = await apiService.create(config.CREATESERIESFAREBULK, fareGroups[key]);
                    if (response.success) {
                        totalCreated += response.createdCount;
                    } else {
                        totalFailed += response.failedCount;
                    }
                } catch (error) {
                    console.error('Error creating fares for group:', error);
                    totalFailed += fareGroups[key].items.length;
                }
            }
            
            if (totalFailed === 0) {
                alert(`✅ Successfully created ${totalCreated} series fares!`);
                resetForm();
            } else {
                alert(`⚠️ Created ${totalCreated} fares, failed to create ${totalFailed} fares.`);
            }
        } catch (error) {
            console.error('Error creating bulk fares:', error);
            alert('❌ Error creating fares. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.dropdown-container')) {
                setShowFromDropdown(false);
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Get selected route-airlines count
    const selectedCount = Object.keys(routeAirlineFares).filter(key => routeAirlineFares[key]?.selected).length;

    // Get filtered destinations
    const getFilteredDestinations = () => {
        const destinations = {};
        routes.forEach(route => {
            // Filter by destination code if filter is applied
            if (destinationFilter && !route.to.toLowerCase().includes(destinationFilter.toLowerCase())) {
                return;
            }
            
            if (!destinations[route.to]) {
                destinations[route.to] = {
                    to: route.to,
                    airlines: route.airlines,
                    count: route.airlines.length
                };
            }
        });
        return Object.values(destinations);
    };

    // Get all route-airline combinations for selected destinations
    const getRouteAirlineCombinations = () => {
        return Object.values(routeAirlineFares).filter(item => {
            if (destinationFilter && !item.to.toLowerCase().includes(destinationFilter.toLowerCase())) {
                return false;
            }
            return true;
        });
    };

    useEffect(() => {
        if (user && !loading) {
            // Nothing to fetch on load
        }
    }, [user, loading]);

    if (loading) return <p>Loading...</p>;

    if (!user) {
        navigate("/");
    }

    // Calculate totals in user's currency
    const totalRevenuePotential = Object.keys(routeAirlineFares)
        .filter(key => routeAirlineFares[key].selected)
        .reduce((sum, key) => {
            const fare = routeAirlineFares[key];
            const pricePerPerson = parseFloat(fare.pricePerPerson) || 0;
            const seats = parseInt(fare.seats) || 0;
            const totalPrice = pricePerPerson * seats;
            return sum + totalPrice;
        }, 0);

    const totalSeats = Object.keys(routeAirlineFares)
        .filter(key => routeAirlineFares[key].selected)
        .reduce((sum, key) => sum + (parseInt(routeAirlineFares[key].seats) || 0), 0);

    const filteredDestinations = getFilteredDestinations();
    const routeAirlineCombinations = getRouteAirlineCombinations();

    return (
        <div id="layout-wrapper">
            <Header User={user} />
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        <div className="card">
                            <div className="p-2">
                                {/* <SeriesFareTabs pageName="SERIESFARECREATE" /> */}
                            </div>
                            <div className="card-body">
                                <div className="row mb-4">
                                    <div className="col-12">
                                        <div className="d-flex align-items-center mb-4">
                                            <div className="flex-grow-1">
                                                <h5 className="card-title text-primary mb-0">
                                                    <RiPlaneLine className="me-2" />
                                                    Create Series Fares by Airline
                                                </h5>
                                                <p className="text-muted mb-0">Each airline on each route can have different fares and dates</p>
                                            </div>
                                            <div className="btn-group">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-primary"
                                                    onClick={() => setShowBulkUpdate(!showBulkUpdate)}
                                                    disabled={selectedCount === 0}
                                                >
                                                    <RiSettingsLine className="me-1" /> Bulk Update
                                                </button>
                                            </div>
                                        </div>
                                        
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="dropdown-container">
                                                    <label className="form-label fw-bold">
                                                        Departure Airport *
                                                    </label>
                                                    <div className="input-group">
                                                        <span className="input-group-text bg-light">
                                                            <RiSearchLine />
                                                        </span>
                                                        <input
                                                            ref={fromInputRef}
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Search airport (e.g., DEL for Delhi)..."
                                                            value={fromSearch}
                                                            onChange={(e) => {
                                                                setFromSearch(e.target.value);
                                                                setShowFromDropdown(true);
                                                            }}
                                                            onFocus={() => setShowFromDropdown(true)}
                                                            required
                                                        />
                                                        <button
                                                            type="button"
                                                            className="btn btn-danger"
                                                            onClick={resetForm}
                                                            title="Reset All"
                                                        >
                                                            <RiCloseLine /> Reset
                                                        </button>
                                                    </div>
                                                    
                                                    {showFromDropdown && filteredFromAirports.length > 0 && (
                                                        <div className="position-relative">
                                                            <div className="dropdown-menu show w-100 mt-1" style={{ zIndex: 1050 }}>
                                                                {filteredFromAirports.map((airport) => (
                                                                    airport && (
                                                                        <button
                                                                            key={airport.airportCode}
                                                                            type="button"
                                                                            className="dropdown-item"
                                                                            onClick={() => handleFromSelect(airport)}
                                                                        >
                                                                            <div>
                                                                                <strong>{airport.cityName} ({airport.airportCode})</strong>
                                                                                <div className="small text-muted">
                                                                                    {airport.airportName}, {airport.countryName}
                                                                                </div>
                                                                            </div>
                                                                        </button>
                                                                    )
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {showBulkUpdate && selectedCount > 0 && (
                                    <div className="card border-warning mb-4">
                                        <div className="card-header bg-warning bg-opacity-10">
                                            <h6 className="mb-0">
                                                <RiSettingsLine className="me-2" />
                                                Bulk Update Selected Items ({selectedCount} selected)
                                            </h6>
                                        </div>
                                        <div className="card-body">
                                            <div className="row g-3">
                                                <div className="col-md-2">
                                                    <label className="form-label small">Departure Date</label>
                                                    <DatePicker
                                                        selected={bulkUpdateValues.departureDate ? new Date(bulkUpdateValues.departureDate) : new Date()}
                                                        onChange={handleDepartureDateChange}
                                                        customInput={<CustomDateInput />}
                                                        dateFormat="dd-MMM-yyyy"
                                                        showYearDropdown
                                                        showMonthDropdown
                                                        dropdownMode="select"
                                                        minDate={new Date()}
                                                        yearDropdownItemNumber={50}
                                                        scrollableYearDropdown
                                                        placeholderText="Select date"
                                                    />
                                                </div>
                                                <div className="col-md-2">
                                                    <label className="form-label small">Valid From</label>
                                                    <DatePicker
                                                        selected={bulkUpdateValues.validFrom ? new Date(bulkUpdateValues.validFrom) : new Date()}
                                                        onChange={handleValidFromChange}
                                                        customInput={<CustomDateInput />}
                                                        dateFormat="dd-MMM-yyyy"
                                                        showYearDropdown
                                                        showMonthDropdown
                                                        dropdownMode="select"
                                                        minDate={new Date()}
                                                        yearDropdownItemNumber={50}
                                                        scrollableYearDropdown
                                                        placeholderText="Select date"
                                                    />
                                                </div>
                                                <div className="col-md-2">
                                                    <label className="form-label small">Valid To</label>
                                                    <DatePicker
                                                        selected={bulkUpdateValues.validTo ? new Date(bulkUpdateValues.validTo) : new Date(oneDayBeforeDate)}
                                                        onChange={handleValidToChange}
                                                        customInput={<CustomDateInput />}
                                                        dateFormat="dd-MMM-yyyy"
                                                        showYearDropdown
                                                        showMonthDropdown
                                                        dropdownMode="select"
                                                        minDate={bulkUpdateValues.validFrom ? new Date(bulkUpdateValues.validFrom) : new Date()}
                                                        yearDropdownItemNumber={50}
                                                        scrollableYearDropdown
                                                        placeholderText="Select date"
                                                    />
                                                </div>
                                                
                                                <div className="col-md-2">
                                                    <label className="form-label small">Cabin Class</label>
                                                    <select
                                                        className="form-control form-control-sm"
                                                        value={bulkUpdateValues.cabinClass}
                                                        onChange={(e) => setBulkUpdateValues(prev => ({...prev, cabinClass: e.target.value}))}
                                                    >
                                                        <option value="">No Change</option>
                                                        <option value="ECONOMY">Economy</option>
                                                        <option value="BUSINESS">Business</option>
                                                        <option value="FIRST">First Class</option>
                                                        <option value="PREMIUM_ECONOMY">Premium Economy</option>
                                                    </select>
                                                </div>
                                                <div className="col-md-2">
                                                    <label className="form-label small">Price Per Person ({userCurrency})</label>
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm"
                                                        value={bulkUpdateValues.pricePerPerson}
                                                        onChange={(e) => setBulkUpdateValues(prev => ({...prev, pricePerPerson: e.target.value}))}
                                                        placeholder="Leave empty to skip"
                                                        min="0"
                                                        step="0.01"
                                                    />
                                                </div>
                                                <div className="col-md-2">
                                                    <label className="form-label small">Seats</label>
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm"
                                                        value={bulkUpdateValues.seats}
                                                        onChange={(e) => setBulkUpdateValues(prev => ({...prev, seats: e.target.value}))}
                                                        placeholder="Leave empty to skip"
                                                        min="0"
                                                        step="1"
                                                    />
                                                </div>
                                                <div className="col-md-2">
                                                    <div className="d-flex align-items-end h-100">
                                                        <button
                                                            type="button"
                                                            className="btn btn-warning btn-sm w-100"
                                                            onClick={applyBulkUpdate}
                                                        >
                                                            Apply to All Selected
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {fetchingRoutes ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-2">Loading airlines and routes from {selectedFromAirport?.airportCode}...</p>
                                    </div>
                                ) : routeAirlineCombinations.length > 0 && (
                                    <form onSubmit={handleBulkSubmit}>
                                        <div className="d-flex justify-content-between align-items-center mb-4 p-3 bg-light rounded">
                                            <div className="d-flex align-items-center gap-3">
                                                <div>
                                                    <label className="form-label small mb-0">
                                                        Filter Destination
                                                        <span className="text-muted ms-2">
                                                            ({filteredDestinations.length} destinations × {routeAirlineCombinations.length} airlines)
                                                        </span>
                                                    </label>
                                                    <div className="input-group input-group-sm" style={{width: '300px'}}>
                                                        <span className="input-group-text">
                                                            <RiFilterLine />
                                                        </span>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder={`Filter by destination code... (${filteredDestinations.length} destinations)`}
                                                            value={destinationFilter}
                                                            onChange={(e) => setDestinationFilter(e.target.value)}
                                                        />
                                                        {destinationFilter && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-secondary"
                                                                onClick={clearDestinationFilter}
                                                                title="Clear filter"
                                                            >
                                                                <RiCloseLine />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="btn-group">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={selectAllRouteAirlines}
                                                    >
                                                        <RiCheckboxCircleLine className="me-1" /> Select All
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-secondary"
                                                        onClick={deselectAllRouteAirlines}
                                                    >
                                                        <RiCheckboxBlankCircleLine className="me-1" /> Deselect All
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="badge bg-primary">
                                                    {selectedCount} of {routeAirlineCombinations.length} selected
                                                </span>
                                                <div className="btn-group">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-secondary"
                                                        onClick={scrollToTop}
                                                        title="Scroll to top"
                                                    >
                                                        <RiArrowUpLine />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-secondary"
                                                        onClick={scrollToBottom}
                                                        title="Scroll to bottom"
                                                    >
                                                        <RiArrowDownLine />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="alert alert-info mb-3">
                                            <RiInformationLine className="me-2" />
                                            <strong>Note:</strong> Please select airline-route combinations manually according to your needs. All fares are in {userCurrency} currency.
                                            <br />
                                            <small> When you select Departure Date, you'll be asked how many days before to set Valid To date.</small>
                                        </div>
                                        
                                        <div 
                                            ref={mainContentRef}
                                            className="mb-4 position-relative"
                                            style={{
                                                maxHeight: '500px',
                                                overflowY: 'auto',
                                                border: '1px solid #dee2e6',
                                                borderRadius: '0.375rem'
                                            }}
                                        >
                                            {filteredDestinations.map((destination, destIndex) => {
                                                const airlinesForDestination = routeAirlineCombinations
                                                    .filter(item => item.to === destination.to)
                                                    .sort((a, b) => a.airlineCode.localeCompare(b.airlineCode));
                                                
                                                return (
                                                    <div key={destIndex} className="border-bottom">
                                                        <div 
                                                            className="sticky-top bg-white border-bottom px-3 py-2"
                                                            style={{
                                                                top: '0',
                                                                zIndex: 10,
                                                                marginTop: '-1px'
                                                            }}
                                                        >
                                                            <div className="d-flex align-items-center justify-content-between">
                                                                <div className="d-flex align-items-center">
                                                                    <span className="badge bg-primary me-2 fs-6">{destination.to}</span>
                                                                    <span className="me-2">Destination</span>
                                                                    <span className="badge bg-info">{destination.count} airlines</span>
                                                                </div>
                                                                <div className="d-flex align-items-center">
                                                                    <small className="text-muted me-3">
                                                                        {airlinesForDestination.filter(a => 
                                                                            routeAirlineFares[`${a.to}_${a.airlineCode}`]?.selected
                                                                        ).length} of {destination.count} selected
                                                                    </small>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={airlinesForDestination.every(a => 
                                                                            routeAirlineFares[`${a.to}_${a.airlineCode}`]?.selected
                                                                        )}
                                                                        onChange={(e) => {
                                                                            airlinesForDestination.forEach(airline => {
                                                                                const key = `${airline.to}_${airline.airlineCode}`;
                                                                                toggleRouteAirlineSelection(key);
                                                                            });
                                                                        }}
                                                                        className="form-check-input"
                                                                    />
                                                                    <span className="ms-2 small">All</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="px-3">
                                                            <table className="table table-hover table-sm mb-0">
                                                                <thead>
                                                                    <tr>
                                                                        <th width="40"></th>
                                                                        <th width="120">Airline</th>
                                                                        <th width="120">Departure Date *</th>
                                                                        <th width="120">Valid From *</th>
                                                                        <th width="120">Valid To *</th>
                                                                        <th width="100">Class *</th>
                                                                        <th width="120">Price Per Person *</th>
                                                                        <th width="80">Seats *</th>
                                                                        <th width="120">Total Price</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {airlinesForDestination.map((airline, airlineIndex) => {
                                                                        const key = `${airline.to}_${airline.airlineCode}`;
                                                                        const fare = routeAirlineFares[key] || {};
                                                                        const pricePerPerson = parseFloat(fare.pricePerPerson) || 0;
                                                                        const seats = parseInt(fare.seats) || 0;
                                                                        const totalPrice = pricePerPerson * seats;
                                                                        
                                                                        return (
                                                                            <tr key={airlineIndex} className={fare.selected ? '' : 'table-secondary'}>
                                                                                <td>
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        checked={fare.selected || false}
                                                                                        onChange={() => toggleRouteAirlineSelection(key)}
                                                                                        className="form-check-input"
                                                                                    />
                                                                                </td>
                                                                                <td>
                                                                                    <div>
                                                                                        <strong className="text-primary">{fare.airlineCode}</strong>
                                                                                        <div className="text-muted small">
                                                                                            {fare.airlineName}
                                                                                        </div>
                                                                                    </div>
                                                                                </td>
                                                                                <td>
                                                                                    <DatePicker
                                                                                        selected={fare.departureDate ? new Date(fare.departureDate) : new Date()}
                                                                                        onChange={(date) => handleRouteAirlineDateChange(key, 'departureDate', date)}
                                                                                        customInput={<CustomDateInput />}
                                                                                        dateFormat="dd-MMM-yyyy"
                                                                                        showYearDropdown
                                                                                        showMonthDropdown
                                                                                        dropdownMode="select"
                                                                                        minDate={new Date()}
                                                                                        yearDropdownItemNumber={50}
                                                                                        scrollableYearDropdown
                                                                                        placeholderText="Select date"
                                                                                        disabled={!fare.selected}
                                                                                        required={fare.selected}
                                                                                    />
                                                                                </td>
                                                                                <td>
                                                                                    <DatePicker
                                                                                        selected={fare.validFrom ? new Date(fare.validFrom) : new Date()}
                                                                                        onChange={(date) => handleRouteAirlineDateChange(key, 'validFrom', date)}
                                                                                        customInput={<CustomDateInput />}
                                                                                        dateFormat="dd-MMM-yyyy"
                                                                                        showYearDropdown
                                                                                        showMonthDropdown
                                                                                        dropdownMode="select"
                                                                                        minDate={new Date()}
                                                                                        yearDropdownItemNumber={50}
                                                                                        scrollableYearDropdown
                                                                                        placeholderText="Select date"
                                                                                        disabled={!fare.selected}
                                                                                        required={fare.selected}
                                                                                    />
                                                                                </td>
                                                                                <td>
                                                                                    <DatePicker
                                                                                        selected={fare.validTo ? new Date(fare.validTo) : new Date(oneDayBeforeDate)}
                                                                                        onChange={(date) => handleRouteAirlineDateChange(key, 'validTo', date)}
                                                                                        customInput={<CustomDateInput />}
                                                                                        dateFormat="dd-MMM-yyyy"
                                                                                        showYearDropdown
                                                                                        showMonthDropdown
                                                                                        dropdownMode="select"
                                                                                        minDate={fare.validFrom ? new Date(fare.validFrom) : new Date()}
                                                                                        yearDropdownItemNumber={50}
                                                                                        scrollableYearDropdown
                                                                                        placeholderText="Select date"
                                                                                        disabled={!fare.selected}
                                                                                        required={fare.selected}
                                                                                    />
                                                                                </td>
                                                                                <td>
                                                                                    <select
                                                                                        className="form-control form-control-sm"
                                                                                        value={fare.cabinClass || 'ECONOMY'}
                                                                                        onChange={(e) => handleRouteAirlineFareChange(key, 'cabinClass', e.target.value)}
                                                                                        disabled={!fare.selected}
                                                                                        required={fare.selected}
                                                                                    >
                                                                                        <option value="ECONOMY">Economy</option>
                                                                                        <option value="BUSINESS">Business</option>
                                                                                        <option value="FIRST">First Class</option>
                                                                                        <option value="PREMIUM_ECONOMY">Premium Economy</option>
                                                    </select>
                                                                                </td>
                                                                                <td>
                                                                                    <div className="input-group input-group-sm">
                                                                                        <span className="input-group-text">{userCurrency}</span>
                                                                                        <input
                                                                                            type="number"
                                                                                            className="form-control"
                                                                                            value={fare.pricePerPerson || ''}
                                                                                            onChange={(e) => handleRouteAirlineFareChange(key, 'pricePerPerson', e.target.value)}
                                                                                            min="0"
                                                                                            step="0.01"
                                                                                            placeholder="0"
                                                                                            disabled={!fare.selected}
                                                                                            required={fare.selected}
                                                                                        />
                                                                                    </div>
                                                                                </td>
                                                                                <td>
                                                                                    <input
                                                                                        type="number"
                                                                                        className="form-control form-control-sm"
                                                                                        value={fare.seats || ''}
                                                                                        onChange={(e) => handleRouteAirlineFareChange(key, 'seats', e.target.value)}
                                                                                        min="0"
                                                                                        placeholder="0"
                                                                                        disabled={!fare.selected}
                                                                                        required={fare.selected}
                                                                                    />
                                                                                </td>
                                                                                <td>
                                                                                    <div className="input-group input-group-sm">
                                                                                        <span className="input-group-text bg-light">{userCurrency}</span>
                                                                                        <input
                                                                                            type="text"
                                                                                            className="form-control bg-light"
                                                                                            value={totalPrice.toLocaleString('en-IN', {
                                                                                                minimumFractionDigits: 2,
                                                                                                maximumFractionDigits: 2
                                                                                            })}
                                                                                            readOnly
                                                                                            disabled={!fare.selected}
                                                                                        />
                                                                                    </div>
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        
                                        {selectedCount > 0 && (
                                            <div className="card border-success mb-4">
                                                <div className="card-body bg-success bg-opacity-10">
                                                    <div className="row align-items-center">
                                                        <div className="col-md-8">
                                                            <h6 className="card-title text-success mb-3">
                                                                <RiCheckboxCircleLine className="me-2" />
                                                                Ready to Create {selectedCount} Series Fares
                                                            </h6>
                                                            <div className="row">
                                                                <div className="col-4">
                                                                    <div className="text-muted small">Airline-Route Combos</div>
                                                                    <div className="h4">{selectedCount}</div>
                                                                </div>
                                                                <div className="col-4">
                                                                    <div className="text-muted small">Total Seats</div>
                                                                    <div className="h4">{totalSeats}</div>
                                                                </div>
                                                                <div className="col-4">
                                                                    <div className="text-muted small">Revenue Potential</div>
                                                                    <div className="h3 text-success">
                                                                        {userCurrency} {totalRevenuePotential.toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4 text-end">
                                                            <button
                                                                type="submit"
                                                                className="btn btn-success btn-lg px-5 py-3"
                                                                disabled={isSaving}
                                                            >
                                                                {isSaving ? (
                                                                    <>
                                                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                                                        Creating...
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <RiSaveLine className="me-2" />
                                                                        Create {selectedCount} Fares
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {selectedCount === 0 && routeAirlineCombinations.length > 0 && (
                                            <div className="alert alert-warning text-center">
                                                <RiCheckboxBlankCircleLine className="me-2" />
                                                Please select at least one airline-route combination
                                            </div>
                                        )}
                                    </form>
                                )}
                                
                                {!fetchingRoutes && !selectedFromAirport && (
                                    <div className="text-center py-5">
                                        <div className="display-1 text-muted mb-3">
                                            <RiPlaneLine />
                                        </div>
                                        <h5>Search for Departure Airport</h5>
                                        <p className="text-muted">
                                            Enter airport code to see all airlines and routes
                                        </p>
                                    </div>
                                )}
                                
                                {!fetchingRoutes && selectedFromAirport && routeAirlineCombinations.length === 0 && (
                                    <div className="alert alert-info text-center">
                                        <RiPlaneLine className="me-2" />
                                        No airlines/routes found from {selectedFromAirport.cityName} ({selectedFromAirport.airportCode})
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showDaysModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <RiCalendarCheckLine className="me-2" />
                                    Set Valid To Date
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowDaysModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p>
                                    How many days before the departure date should Valid To be set?
                                    <br />
                                    <small className="text-muted">
                                        Departure Date: {currentDateContext.date ? new Date(currentDateContext.date).toLocaleDateString('en-GB', {
                                            day: '2-digit',
                                            month: 'short',
                                            year: 'numeric'
                                        }) : ''}
                                    </small>
                                </p>
                                
                                <div className="row">
                                    {daysOptions.map(days => (
                                        <div className="col-3 mb-2" key={days}>
                                            <button
                                                type="button"
                                                className={`btn w-100 ${selectedDaysBefore === days ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => setSelectedDaysBefore(days)}
                                            >
                                                {days} {days === 1 ? 'day' : 'days'}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                
                                <div className="mt-3">
                                    <label className="form-label">Or enter custom number of days:</label>
                                    <div className="input-group">
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={selectedDaysBefore}
                                            onChange={(e) => setSelectedDaysBefore(parseInt(e.target.value) || 1)}
                                            min="1"
                                            max="730"
                                        />
                                        <span className="input-group-text">days before</span>
                                    </div>
                                </div>
                                
                                <div className="alert alert-info mt-3">
                                    <strong>Example:</strong> If Departure Date is {currentDateContext.date ? new Date(currentDateContext.date).toLocaleDateString('en-GB', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric'
                                    }) : ''} and you select {selectedDaysBefore} {selectedDaysBefore === 1 ? 'day' : 'days'},
                                    then Valid To will be set to {currentDateContext.date ? new Date(new Date(currentDateContext.date).setDate(new Date(currentDateContext.date).getDate() - selectedDaysBefore)).toLocaleDateString('en-GB', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric'
                                    }) : ''}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={() => setShowDaysModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleDaysSelect}
                                >
                                    Set Valid To Date
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SeriesFare;